import React, { useMemo, useState } from 'react';
import { PageHeader } from '../components/dashboard/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Megaphone, FileText, ImageIcon, Video, EyeOff, Send, Sparkles } from 'lucide-react';
import { Button } from '../components/Button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/Select';
import { Textarea } from '../components/Textarea';
import { Label } from '../components/Label';
import { Badge } from '../components/Badge';
import { toast } from 'sonner';
import { DEMO_ISSUES } from '../lib/demoData';
import { RelatedWorkflow } from '../components/dashboard/RelatedWorkflow';
import { Briefcase, FileSearch, Activity } from 'lucide-react';
type Response = {
  type: string;
  icon: any;
  tone: string;
  impact: number;
  sample: string;
};
// Generate a contextual response set based on the selected issue
function buildResponses(issueTitle: string, category: string): Response[] {
  return [{
    type: 'Klarifikasi Resmi',
    icon: FileText,
    tone: 'Faktual & Empatik',
    impact: 78,
    sample: `Pemerintah Republik Indonesia menyikapi dengan serius isu "${issueTitle}". Berdasarkan verifikasi tim lintas kementerian, narasi yang beredar telah disandingkan dengan data resmi dan akan diluruskan melalui kanal terpercaya. Data lengkap, sumber asli, dan kontak hotline dapat diakses publik mulai pukul 14:00 WIB hari ini.`
  }, {
    type: 'Pernyataan Pers',
    icon: Megaphone,
    tone: 'Formal & Tegas',
    impact: 65,
    sample: `Sehubungan dengan beredarnya informasi mengenai "${issueTitle}", pemerintah menyampaikan klarifikasi resmi. Tindakan hukum sedang disiapkan terhadap pelaku penyebar informasi palsu sesuai UU ITE dan UU PDP 27/2022.`
  }, {
    type: 'Infografis Data',
    icon: ImageIcon,
    tone: 'Visual & Mudah Dipahami',
    impact: 82,
    sample: `Visualisasi 1 lembar untuk isu "${issueTitle}": ringkasan data resmi, lokasi terdampak, timeline kronologis, dan kontak verifikasi. Format optimal untuk distribusi Instagram, X, dan WhatsApp.`
  }, {
    type: 'Video Pendek',
    icon: Video,
    tone: 'Personal & Manusiawi',
    impact: 88,
    sample: `Video vertikal 60 detik dengan narasumber kredibel + pelibatan masyarakat terdampak isu "${issueTitle}". Dilengkapi caption multibahasa daerah & subtitle untuk TikTok, Reels, dan YouTube Shorts.`
  }, {
    type: 'Diam Strategis',
    icon: EyeOff,
    tone: 'Wait-and-See',
    impact: category === 'KEAMANAN' ? 12 : 28,
    sample: `Tidak merespons publik selama 6-12 jam, pantau perkembangan. Hanya direkomendasikan jika isu menunjukkan tren menurun organik dan tidak ada eskalasi resiko keamanan.`
  }];
}
const impactTone = (impact: number) => {
  if (impact >= 75) return 'text-emerald-600';
  if (impact >= 50) return 'text-amber-600';
  return 'text-red-600';
};
export function ResponseRecommender() {
  const [issueId, setIssueId] = useState(DEMO_ISSUES[0].id);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showResults, setShowResults] = useState(true);
  const selectedIssue = useMemo(() => DEMO_ISSUES.find(i => i.id === issueId) ?? DEMO_ISSUES[0], [issueId]);
  const responses = useMemo(() => buildResponses(selectedIssue.title, selectedIssue.category), [selectedIssue]);
  const handleGenerate = () => {
    setIsGenerating(true);
    setShowResults(false);
    setTimeout(() => {
      setIsGenerating(false);
      setShowResults(true);
      toast.success('Draf respons berhasil di-generate', {
        description: `5 opsi format untuk: ${selectedIssue.title}`
      });
    }, 1500);
  };
  return <div className="mx-auto flex w-full max-w-[1600px] flex-col gap-6" data-id="element-3100">
      <PageHeader group="Komando & Respons" title="Official Response Recommendation Engine" description="Rekomendasi respons resmi multi-format berdasarkan jenis isu, target audiens, dan tingkat urgensi." icon={Megaphone} data-id="element-3101" />

      {/* Issue selector + context */}
      <Card className="bg-card border-border/60" data-id="element-3102">
        <CardContent className="p-5" data-id="element-3103">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end" data-id="element-3104">
            <div className="lg:col-span-7" data-id="element-3105">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground" data-id="element-3106">
                Pilih Isu Prioritas
              </Label>
              <Select value={issueId} onValueChange={setIssueId} data-id="element-3107">
                <SelectTrigger className="mt-1.5 h-10" data-id="element-3108">
                  <SelectValue data-id="element-3109">
                    <span className="truncate" data-id="element-3110">{selectedIssue.title}</span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-w-[600px]" data-id="element-3111">
                  {DEMO_ISSUES.map(i => <SelectItem key={i.id} value={i.id} data-id="element-3112">
                      <div className="flex items-center gap-2 py-0.5" data-id="element-3113">
                        <span className={`size-1.5 rounded-full shrink-0 ${i.severity === 'KRITIS' ? 'bg-red-500' : i.severity === 'TINGGI' ? 'bg-amber-500' : 'bg-blue-500'}`} data-id="element-3114" />
                        <span className="text-sm" data-id="element-3115">{i.title}</span>
                        <span className="font-mono text-[9px] text-muted-foreground ml-auto" data-id="element-3116">
                          {i.updatedAt}
                        </span>
                      </div>
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="lg:col-span-5 flex items-end justify-end gap-2" data-id="element-3117">
              <Button onClick={handleGenerate} disabled={isGenerating} className="h-10" data-id="element-3118">
                <Sparkles className="size-3.5 mr-1.5" data-id="element-3119" />
                {isGenerating ? 'Menganalisis...' : 'Generate Draf Respons'}
              </Button>
            </div>
          </div>

          {/* Issue context strip */}
          <div className="mt-4 pt-4 border-t border-border/60 grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-3120">
            <div data-id="element-3121">
              <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground" data-id="element-3122">
                Kategori
              </p>
              <Badge variant="outline" className="mt-1 font-mono text-[10px] bg-muted/30 border-border/60" data-id="element-3123">
                {selectedIssue.category}
              </Badge>
            </div>
            <div data-id="element-3124">
              <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground" data-id="element-3125">
                Severity
              </p>
              <Badge variant="outline" className={`mt-1 font-mono text-[10px] border-none ${selectedIssue.severity === 'KRITIS' ? 'bg-red-50 text-red-700' : selectedIssue.severity === 'TINGGI' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`} data-id="element-3126">
                {selectedIssue.severity}
              </Badge>
            </div>
            <div data-id="element-3127">
              <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground" data-id="element-3128">
                Volume
              </p>
              <p className="font-mono text-xs font-bold mt-1" data-id="element-3129">
                {selectedIssue.volume}
              </p>
            </div>
            <div data-id="element-3130">
              <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground" data-id="element-3131">
                PIC K/L
              </p>
              <p className="font-mono text-xs font-bold mt-1 truncate" data-id="element-3132">
                {selectedIssue.ministry}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {isGenerating && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3133">
          {[1, 2, 3].map(i => <Card key={i} className="bg-card border-border/60 animate-pulse" data-id="element-3134">
              <CardContent className="p-5" data-id="element-3135">
                <div className="h-4 bg-muted rounded w-1/3 mb-4" data-id="element-3136" />
                <div className="space-y-2" data-id="element-3137">
                  <div className="h-3 bg-muted rounded w-full" data-id="element-3138" />
                  <div className="h-3 bg-muted rounded w-5/6" data-id="element-3139" />
                  <div className="h-3 bg-muted rounded w-4/6" data-id="element-3140" />
                </div>
              </CardContent>
            </Card>)}
        </div>}

      {showResults && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-3141">
          {responses.map(r => <Card key={r.type} className="bg-card border-border/60 flex flex-col hover:border-primary/40 hover:shadow-sm transition" data-id="element-3142">
              <CardHeader className="border-b border-border/60 py-3 flex flex-row items-center justify-between space-y-0" data-id="element-3143">
                <CardTitle className="text-sm font-bold flex items-center gap-2" data-id="element-3144">
                  <r.icon className="size-4 text-primary" data-id="element-3145" />
                  {r.type}
                </CardTitle>
                <span className={`font-mono text-[10px] font-bold ${impactTone(r.impact)}`} data-id="element-3146">
                  Impact: {r.impact}/100
                </span>
              </CardHeader>
              <CardContent className="p-4 space-y-3" data-id="element-3147">
                <div className="flex items-center justify-between" data-id="element-3148">
                  <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground" data-id="element-3149">
                    Tone: {r.tone}
                  </p>
                </div>
                <Textarea defaultValue={r.sample} className="bg-muted/20 border-border/60 min-h-[140px] text-xs leading-relaxed" data-id="element-3150" />
                <div className="flex gap-2" data-id="element-3151">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => toast.success('Disimpan ke Draft')} data-id="element-3152">
                    Simpan ke Draft
                  </Button>
                  <Button size="sm" className="flex-1" onClick={() => toast.success('Dikirim ke Platform', {
              description: `Format ${r.type} dipublikasikan ke kanal resmi.`
            })} data-id="element-3153">
                    <Send className="size-3.5 mr-1.5" data-id="element-3154" /> Kirim
                  </Button>
                </div>
              </CardContent>
            </Card>)}
        </div>}

      <RelatedWorkflow currentStep="Rekomendasi Respons" nextSteps={[{
      to: '/command-tasking',
      icon: Briefcase,
      label: 'Eskalasi Tasking K/L',
      desc: 'Kirim respons terpilih sebagai tasking ke kementerian terkait.'
    }, {
      to: '/case-management',
      icon: FileSearch,
      label: 'Buka Berkas Kasus',
      desc: 'Hubungkan respons ke case file aktif.'
    }, {
      to: '/crisis-simulation',
      icon: Activity,
      label: 'Simulasi Krisis',
      desc: 'Uji skenario respons sebelum publikasi resmi.'
    }]} data-id="element-3155" />
    </div>;
}